import type { PatientOut } from '@/features/emr/api/patients-api';
import type { PregnancyOut } from '@/features/emr/types';
import type { LabResult } from '@/features/labs/api';
import { formatInstant } from '@/lib/utils/date';
import { activeEvents } from './projection';
import { dayNumber, formatGa, gaDays, weightGain } from './calculations';
import type { ClinicalEvent, ClinicalSubject } from './types';
const LB_GRAMS = 453.59237;
const recorded = (value: unknown) => (typeof value === 'string' && value.trim() ? value.trim() : null);
export function pregnancyHeaderModel(
  pregnancy: PregnancyOut,
  patient: PatientOut | undefined,
  events: ClinicalEvent[],
  subjects: ClinicalSubject[],
  timeZone: string,
  now = new Date(),
) {
  const metadata = patient?.id === pregnancy.patient_id ? patient.metadata_ : null;
  const today = formatInstant(now.toISOString(), 'yyyy-MM-dd', timeZone);
  const dob = recorded(metadata?.date_of_birth);
  const age =
    dob && dayNumber(dob) !== null && dob <= today
      ? Number(today.slice(0, 4)) - Number(dob.slice(0, 4)) - (today.slice(5) < dob.slice(5) ? 1 : 0)
      : null;
  const motherIds = new Set(subjects.filter((s) => s.subject_type === 'MOTHER').map((s) => s.id));
  const observations = activeEvents(events).filter(
    (e) => motherIds.has(e.subject_id) && Date.parse(e.occurred_at) <= now.getTime(),
  );
  const weights = observations
    .filter(
      (e) =>
        e.event_type === 'VITALS.WEIGHT' &&
        typeof e.payload.grams === 'number' &&
        Number.isFinite(e.payload.grams) &&
        e.payload.grams > 0,
    )
    .sort((a, b) => Date.parse(b.occurred_at) - Date.parse(a.occurred_at) || b.sequence - a.sequence);
  const current = weights[0];
  const baseline =
    pregnancy.pre_pregnancy_weight != null &&
    Number.isFinite(pregnancy.pre_pregnancy_weight) &&
    pregnancy.pre_pregnancy_weight > 0
      ? pregnancy.pre_pregnancy_weight * LB_GRAMS
      : null;
  const gain = weightGain(current?.payload.grams as number | undefined, baseline);
  const allergies = (metadata?.allergies ?? []).filter((item) => item.trim());
  return {
    name: [recorded(metadata?.first_name), recorded(metadata?.last_name)].filter(Boolean).join(' ') || null,
    mrn: patient?.id === pregnancy.patient_id ? recorded(patient.medical_record_number) : null,
    dob,
    age,
    today,
    ga: pregnancy.status === 'active' && pregnancy.edd ? formatGa(gaDays(today, pregnancy.edd)) : null,
    allergies,
    context: observations.filter((e) => e.event_type === 'PRENATAL.CONTEXT').at(-1)?.payload ?? {},
    currentWeight: current
      ? { grams: current.payload.grams as number, date: current.occurred_at, eventId: current.id }
      : null,
    baselineGrams: baseline,
    gainGrams: gain,
    medications: observations.filter((e) => e.event_type === 'MED.ADMIN'),
    decisions: observations.filter((e) => e.event_type === 'CONSENT.DECISION'),
  };
}
export const prenatalLabGroups = [
  {
    label: 'Blood type / Rh',
    names: ['abo', 'abo group', 'blood type', 'abo rh', 'abo and rh', 'abo rh d', 'rh', 'rh type', 'rh factor', 'rh d'],
  },
  { label: 'Antibody screen', names: ['antibody screen', 'antibody screening', 'red blood cell antibody screen'] },
  {
    label: 'GBS',
    names: [
      'gbs',
      'group b strep',
      'group b streptococcus',
      'group b streptococcus culture',
      'group b strep culture',
      'streptococcus group b culture',
    ],
  },
  { label: 'HIV', names: ['hiv', 'hiv screen', 'hiv ag ab', 'hiv 1 2 ag ab', 'hiv 1 2 antigen antibody'] },
  { label: 'Hepatitis B surface antigen', names: ['hbsag', 'hepatitis b surface antigen'] },
  { label: 'Syphilis', names: ['rpr', 'syphilis', 'syphilis screen', 'treponema pallidum antibody'] },
  { label: 'Rubella', names: ['rubella', 'rubella igg', 'rubella antibody igg'] },
] as const;
const normalize = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
/** Exact alias matches only. Unrecognized test names remain visible as additional results. */
export function groupPrenatalLabs(results: LabResult[]) {
  const recognized = new Set<string>();
  const groups = prenatalLabGroups.map((group) => ({
    label: group.label,
    results: results.filter((result) => {
      const matches = (group.names as readonly string[]).includes(normalize(result.test_name));
      if (matches) recognized.add(result.id);
      return matches;
    }),
  }));
  return [
    ...groups,
    { label: 'Additional reviewed tests', results: results.filter((result) => !recognized.has(result.id)) },
  ];
}
export function displayWeight(grams: number) {
  return `${(grams / LB_GRAMS).toFixed(1)} lb / ${(grams / 1000).toFixed(2)} kg`;
}
